import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    alignment = "alignment",
    title = "title",
    links = "links",
    linksColor = "linksColor",
    titleTextColor = "titleTextColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.links]: LinkModel[];
        [fields.linksColor]: string;
        [fields.titleTextColor]: string;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    rightLinksWrapper?: InjectableCss;
    copyrightLinksWrapper?: InjectableCss;
    copyrightLinks?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: none;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            @media (min-width: 1170px) {
                padding-right: 32px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-right: 15px;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    title: {
        css: css`
            font-style: italic;
            font-weight: 400;
            font-size: 14px;
            line-height: 16px;
            color: ${getColor("text.main")};
            @media (min-width: 1170px) {
                font-size: 14px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                        `,
                        null,
                        css`
                            font-size: 9px;
                        `,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    rightLinksWrapper: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 16px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    copyrightLinksWrapper: {
        css: css`
            &:not(:last-child) {
                @media (min-width: 1170px) {
                    padding-right: 24px !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding-right: 14px;
                            `,
                            null,
                        ],
                        "min",
                    )}
            }
        `,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    pageType: getCurrentPage(state).type,
    siteName: state.context.website.name,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const CENCopyrightFooter: FC<Props> = ({ fields, extendedStyles, pageType, siteName }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);
    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
            display: flex;
            justify-content: flex-end;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                            margin-top: 24px;
                        `,
                        null,
                    ],
                    "max",
                )}

            ${styles.linkListWrapper?.css}
        `,
    };
    const alignmentStylesNisco = {
        css: css`
            text-align: ${fields.alignment || "right"};
            display: flex;
            justify-content: flex-end;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                    `,
                    css`
                        // flex-direction: column;
                        // margin-top: 24px;
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                        margin-top: 10px;
                    `,
                ])}

            ${styles.linkListWrapper?.css}
        `,
    };
    const alignmentStylesValue = siteName === "Nisco" ? alignmentStylesNisco : alignmentStyles;

    const copyrightLinks = {
        css: css`
            color: ${fields.linksColor} !important;
            span {
                font-weight: 400;
                font-size: 14px;
                line-height: 20px;
                color: ${fields.linksColor} !important;
                @media (min-width: 1170px) {
                    font-size: 14px !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 12px;
                            `,
                            null,
                            css`
                                font-size: 9px;
                            `,
                            css`
                                font-size: 12px;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    };
    const copyrightLinksNisco = {
        css: css`
            color: ${fields.linksColor} !important;
            span {
                font-weight: 400;
                font-size: 14px;
                line-height: 20px;
                color: ${fields.linksColor} !important;
                @media (min-width: 1170px) {
                    font-size: 14px !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                    ])}
            }
        `,
    };
    const copyrightLinksValue = siteName === "Nisco" ? copyrightLinksNisco : copyrightLinks;
    const rightLinksWrapper = {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 16px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    };
    const rightLinksWrapperNisco = {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            gap: 16px;
                            margin-top: 6px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    };
    const rightLinksWrapperValue = siteName === "Nisco" ? rightLinksWrapperNisco : rightLinksWrapper;

    return (
        <StyledWrapper>
            <StyledWrapper data-test-selector="copyrightFooterLinks" {...alignmentStylesValue}>
                <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                    <Typography className="list-title" {...styles.title} style={{ color: fields.titleTextColor }}>
                        {fields.title}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...rightLinksWrapperValue}>
                    {links.map((link, index) => (
                        <StyledWrapper data-test-selector="linksWrapper" key={index} {...styles.copyrightLinksWrapper}>
                            {link.url && (
                                <Link
                                    className="list-link"
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    {...copyrightLinksValue}
                                >
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            )}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(CENCopyrightFooter),
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.linksColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: `${getColor("text.main")}`,
                displayName: "Link Text Color",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.titleTextColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: `${getColor("text.main")}`,
                displayName: "Title Text Color",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
